import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow } from 'date-fns';
import React, { useState, useMemo } from 'react';
import { CgAttachment } from "react-icons/cg";
import { FiCalendar, FiEdit, FiTag, FiPackage, FiCheck } from 'react-icons/fi';
import { GoArrowDown, GoArrowUp } from "react-icons/go";
import { TiDeleteOutline } from "react-icons/ti";
import StatusBotIcon from "@/components/StatusBotIcon";
import { useAuth } from "@/context/AuthContext";

import CommentRenderer from './CommentRenderer';
import EditableComment from './EditableComment';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    PiCellSignalFullFill,
    PiCellSignalLowFill,
    PiCellSignalMediumFill,
    PiCellSignalNoneFill,
    PiUserCircleDashed
} from "react-icons/pi";
import {
    TbAlertCircle,
    TbCircle,
    TbCircleCheckFilled,
    TbCircleDashed,
    TbCircleHalf2,
    TbX,
    TbReload,
    TbGitCommit,
    TbGitPullRequest,
    TbGitMerge,
    TbGitPullRequestClosed,
    TbBrandGithub
} from "react-icons/tb";

import { Bell, BellOff } from "lucide-react";
import { RiEye2Line } from "react-icons/ri";
import { getIconComponent } from '@/lib/statusIconsMap';
import { STATUS_COLORS } from '@/lib/colorsMap';
import { PRIORITY_MAP } from '@/lib/priorityMap';

const statusConfig = {
    backlog: { name: 'Backlog', icon: TbCircleDashed, color: 'text-gray-500' },
    todo: { name: 'Todo', icon: TbCircle, color: 'text-blue-500' },
    in_progress: { name: 'In Progress', icon: TbCircleHalf2, color: 'text-yellow-500' },
    in_review: { name: 'In Review', icon: RiEye2Line, color: 'text-violet-500' },
    done: { name: 'Done', icon: TbCircleCheckFilled, color: 'text-emerald-500' }
};

// Default configuration for unknown activity types
const defaultConfig = {
    icon: (activity) => FiTag,
    getMessage: (activity) => `performed action: ${activity.type}`
};

// Add this helper function at the top of the file
const wrapInSpan = (text) => `<span class="font-medium text-neutral-700 dark:text-neutral-200">${text}</span>`;
const wrapInLink = (text, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="font-medium text-neutral-700 dark:text-neutral-200 hover:underline">${text}</a>`;

// Add this helper function at the top with other helper functions
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Friendly names for scope values
const SCOPE_DISPLAY_NAMES = {
    tracked: 'Active Week',
    backlog: 'Backlog',
};

export default function TaskActivityTimeline({ activities, isLoading, availableStatuses, task, onTaskChange }) {
    const { user } = useAuth();
    const [editingCommentId, setEditingCommentId] = useState(null);

    const statusConfig = useMemo(() => {
        const config = {};
        availableStatuses && availableStatuses.forEach(status => {
            config[status.id] = { name: status.name, icon: getIconComponent(status.icon) || TbCircle, color: STATUS_COLORS[status.color]?.text || 'text-muted-foreground' };
        });
        return config;
    }, [availableStatuses]);

    const activityConfig = {
        task_created: {
            icon: (activity) => {
                return function TaskCreatedActorIcon() {
                    return (
                        <div className="flex items-center">
                            <div className={`w-4 h-4 rounded-full ${activity.actor.avatarColor} flex items-center justify-center outline outline-white dark:outline-black relative z-10`}>
                                <span className="text-[0.6rem] font-medium text-white">
                                    {activity.actor.initials}
                                </span>
                            </div>
                            {activity.metadata.createdFromSubmission && 
                                <div className="-ml-1 w-4 h-4 bg-teal-600 rounded-full flex items-center justify-center outline outline-white dark:outline-black relative z-0">
                                    <StatusBotIcon className="w-2.5 h-2.5 text-white" />
                                </div>
                            }
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                return `created the task`;
            }
        },
        description_updated: {
            icon: (activity) => FiTag,
            getMessage: (activity) => `updated the description`
        },
        status_changed: {
            icon: (activity) => statusConfig[activity.metadata.to].icon,
            getMessage: (activity) => {
                if (activity.mergedCount > 1) {
                    return `changed the status from ${wrapInSpan(statusConfig[activity.metadata.from].name)} to ${wrapInSpan(statusConfig[activity.metadata.to].name)} (${activity.mergedCount} updates)`;
                }
                return `changed the status from ${wrapInSpan(statusConfig[activity.metadata.from].name)} to ${wrapInSpan(statusConfig[activity.metadata.to].name)}`;
            }
        },
        assignee_changed: {
            icon: (activity) => {
                if (!activity.metadata.toName) {
                    return PiUserCircleDashed;
                }
                return function AssigneeIcon() {
                    return (
                        <div className={`w-4 h-4 rounded-full ${activity.metadata.toAvatarColor} flex items-center justify-center`}>
                            <span className="text-[0.6rem] font-medium text-white">
                                {activity.metadata.toInitials}
                            </span>
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                if (!activity.metadata.toId) {
                    return `unassigned the task`;
                }
                if (activity.metadata.toId === activity.actor.id) {
                    return `self-assigned the task`;
                }
                return `assigned the task to ${wrapInSpan(activity.metadata.toName)}`;
            }
        },
        summary_updated: {
            icon: (activity) => FiEdit,
            getMessage: (activity) => `updated the summary`
        },
        priority_changed: {
            icon: (activity) => PRIORITY_MAP[activity.metadata.to.toLowerCase()].icon,
            getMessage: (activity) => {
                const priorityLevels = {
                    'urgent': 3,
                    'high': 2,
                    'medium': 1,
                    'low': 0,
                    'no_priority': -1
                };
                const fromLevel = priorityLevels[activity.metadata.from.toLowerCase()];
                const toLevel = priorityLevels[activity.metadata.to.toLowerCase()];
                const action = fromLevel < toLevel ? 'raised' : 'lowered';
                return `${action} the priority to ${wrapInSpan(PRIORITY_MAP[activity.metadata.to.toLowerCase()].name)}`;
            }
        },
        due_date_changed: {
            icon: (activity) => FiCalendar,
            getMessage: (activity) => {
                if (!activity.metadata.from && activity.metadata.to) {
                    return `set the due date to ${wrapInSpan(new Date(activity.metadata.to).toLocaleDateString())}`;
                }
                if (activity.metadata.from && !activity.metadata.to) {
                    return `removed the due date`;
                }
                if (activity.metadata.from && activity.metadata.to) {
                    const from = new Date(activity.metadata.from).toLocaleDateString();
                    const to = new Date(activity.metadata.to).toLocaleDateString();
                    return `changed the due date from ${wrapInSpan(from)} to ${wrapInSpan(to)}`;
                }
                return `updated the due date`;  // fallback case
            }
        },
        position_changed: {
            icon: (activity) => {
                if (activity.metadata.to < activity.metadata.from) {
                    return GoArrowUp;
                }
                return GoArrowDown;
            },
            getMessage: (activity) => {
                const fromPosition = activity.metadata.from;
                const toPosition = activity.metadata.to;
                const action = fromPosition > toPosition ? 'moved up' : 'moved down';
                return `${action} the task position`;
            }
        },
        attachment_added: {
            icon: (activity) => CgAttachment,
            getMessage: (activity) => {
                const fileSize = formatFileSize(activity.metadata.fileSize);
                return `attached <span class="font-medium text-neutral-700 dark:text-neutral-200">${activity.metadata.filename}</span> (${fileSize})`;
            }
        },
        attachment_removed: {
            icon: (activity) => TiDeleteOutline,
            getMessage: (activity) => {
                return `removed the attachment <span class="font-medium text-neutral-700 dark:text-neutral-200">${activity.metadata.filename}</span>`;
            }
        },
        label_added: {
            icon: (activity) => FiTag,
            getMessage: (activity) => {
                if (activity.metadata.labels) {
                    const labels = activity.metadata.labels
                        .filter(l => l.action === 'added')
                        .map(l => wrapInSpan(l.name));
                    if (labels.length === 0) return '';
                    return `added ${labels.length > 1 ? 'labels' : 'the label'} ${labels.join(', ')}`;
                }
                // Fallback for single label add
                const labelName = activity.metadata.labelName || activity.metadata.name;
                return labelName ? `added the label ${wrapInSpan(labelName)}` : 'added a label';
            }
        },
        label_removed: {
            icon: (activity) => FiTag,
            getMessage: (activity) => {
                if (activity.metadata.labels) {
                    const labels = activity.metadata.labels
                        .filter(l => l.action === 'removed')
                        .map(l => wrapInSpan(l.name));
                    if (labels.length === 0) return '';
                    return `removed ${labels.length > 1 ? 'labels' : 'the label'} ${labels.join(', ')}`;
                }
                // Fallback for single label removal
                const labelName = activity.metadata.labelName || activity.metadata.name;
                return labelName ? `removed the label ${wrapInSpan(labelName)}` : 'removed a label';
            }
        },
        release_changed: {
            icon: (activity) => FiPackage,
            getMessage: (activity) => {
                const fromId = activity.metadata.fromId;
                const toId = activity.metadata.toId;
                // Prefer explicit names if present; fall back to generic releaseName
                const fromName = activity.metadata.fromReleaseName || activity.metadata.releaseName;
                const toName = activity.metadata.toReleaseName || activity.metadata.releaseName;
                
                if (!fromId && toId) {
                    return `added to release ${wrapInSpan(toName || 'Unknown')}`;
                }
                if (fromId && !toId) {
                    return `removed from release ${wrapInSpan(fromName || 'Unknown')}`;
                }
                if (fromId && toId) {
                    return `moved to release ${wrapInSpan(toName || 'Unknown')}`;
                }
                return `updated release`;
            }
        },
        scope_changed: {
            icon: () => FiTag,
            getMessage: (activity) => {
                const rawFrom = activity.metadata.from;
                const rawTo = activity.metadata.to;
                const fromScope = rawFrom ?? null;
                const toScope = rawTo ?? null;

                const fromName = fromScope ? (SCOPE_DISPLAY_NAMES[fromScope] || fromScope) : null;
                const toName = toScope ? (SCOPE_DISPLAY_NAMES[toScope] || toScope) : null;

                if (!fromScope && toScope) {
                    return `moved to ${wrapInSpan(toName)}`;
                }
                if (fromScope && !toScope) {
                    return `removed from ${wrapInSpan(fromName)}`;
                }
                if (fromScope && toScope && fromScope !== toScope) {
                    return `moved to ${wrapInSpan(toName)}`;
                }
                return `updated scope`;
            }
        },
        user_subscribed: {
            icon: (activity) => {
                if (!activity.metadata.toName) {
                    return function BellIcon() {
                        return <Bell className="w-4 h-4 text-muted-foreground" />;
                    };
                }
                return function SubscribedIcon() {
                    return (
                        <div className={`w-4 h-4 rounded-full ${activity.metadata.toAvatarColor} flex items-center justify-center`}>
                            <span className="text-[0.6rem] font-medium text-white">
                                {activity.metadata.toInitials}
                            </span>
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                if (!activity.metadata.toId) {
                    return `subscribed to the task`;
                }
                if (activity.metadata.toId === activity.actor.id) {
                    return `subscribed to the task`;
                }
                return `subscribed ${wrapInSpan(activity.metadata.toName)} to the task`;
            }
        },
        user_unsubscribed: {
            icon: (activity) => {
                if (!activity.metadata.toName) {
                    return function BellIcon() {
                        return <BellOff className="w-4 h-4 text-muted-foreground" />;
                    };
                }
                return function UnsubscribedIcon() {
                    return (
                        <div className={`w-4 h-4 rounded-full ${activity.metadata.toAvatarColor} flex items-center justify-center`}>
                            <span className="text-[0.6rem] font-medium text-white">
                                {activity.metadata.toInitials}
                            </span>
                        </div>
                    );
                };
            },
            getMessage: (activity) => {
                if (!activity.metadata.toId) {
                    return `unsubscribed from the task`;
                }
                if (activity.metadata.toId === activity.actor.id) {
                    return `unsubscribed from the task`;
                }
                return `unsubscribed ${wrapInSpan(activity.metadata.toName)} from the task`;
            }
        },
        task_completed: {
            icon: (activity) => FiCheck,
            getMessage: (activity) => {
                if (activity.metadata.completedAt) {
                    const date = new Date(activity.metadata.completedAt).toLocaleDateString();
                    return `marked the task as completed on ${wrapInSpan(date)}`;
                }
                return `marked the task as completed`;
            }
        },
        completion_date_changed: {
            icon: (activity) => FiCalendar,
            getMessage: (activity) => {
                const from = new Date(activity.metadata.from).toLocaleDateString();
                const to = new Date(activity.metadata.to).toLocaleDateString();
                return `changed completion date from ${wrapInSpan(from)} to ${wrapInSpan(to)}`;
            }
        },
        task_closed: {
            icon: (activity) => TbX,
            getMessage: (activity) => {
                return `closed the task`;
            }
        },
        task_reopened: {
            icon: (activity) => TbReload,
            getMessage: (activity) => {
                return `reopened the task`;
            }
        },
        // GitHub integration activity types - messages are observational (StatusBot reporting events)
        github_commit_linked: {
            icon: () => TbGitCommit,
            getMessage: (activity) => {
                const sha = activity.metadata.shortSha || activity.metadata.sha?.substring(0, 7);
                const author = activity.metadata.authorUsername || activity.metadata.author;
                const message = activity.metadata.message;
                const truncatedMessage = message && message.length > 50
                    ? message.substring(0, 47) + '...'
                    : message;
                const isManual = activity.actor && !activity.actor.isBot;
                const shaDisplay = activity.metadata.url ? wrapInLink(sha, activity.metadata.url) : wrapInSpan(sha);
                return `linked commit ${shaDisplay}${!isManual && author ? ` by ${wrapInSpan(author)}` : ''}${truncatedMessage ? ` - ${truncatedMessage}` : ''}`;
            }
        },
        github_commit_unlinked: {
            icon: (activity) => TbGitCommit,
            getMessage: (activity) => {
                const sha = activity.metadata.shortSha || activity.metadata.sha?.substring(0, 7);
                return `unlinked commit ${wrapInSpan(sha)}`;
            }
        },
        github_pr_unlinked: {
            icon: (activity) => TbGitPullRequest,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                return `unlinked PR ${wrapInSpan(`#${prNumber}`)}`;
            }
        },
        github_pr_linked: {
            icon: () => TbGitPullRequest,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const title = activity.metadata.title;
                const author = activity.metadata.author;
                const truncatedTitle = title && title.length > 50
                    ? title.substring(0, 47) + '...'
                    : title;
                const isManual = activity.actor && !activity.actor.isBot;
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `linked PR ${prDisplay}${!isManual && author ? ` by ${wrapInSpan(author)}` : ''}${truncatedTitle ? ` - ${truncatedTitle}` : ''}`;
            }
        },
        github_pr_opened: {
            icon: () => TbGitPullRequest,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const title = activity.metadata.title;
                const author = activity.metadata.author;
                const isDraft = activity.metadata.isDraft;
                const truncatedTitle = title && title.length > 50
                    ? title.substring(0, 47) + '...'
                    : title;
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `linked ${isDraft ? 'draft ' : ''}PR ${prDisplay}${author ? ` by ${wrapInSpan(author)}` : ''}${truncatedTitle ? ` - ${truncatedTitle}` : ''}`;
            }
        },
        github_pr_synchronized: {
            icon: () => TbGitPullRequest,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const pusher = activity.metadata.pusher;
                const countText = activity.mergedCount > 1 ? ` (${activity.mergedCount}x)` : '';
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `${pusher ? `${wrapInSpan(pusher)} ` : ''}pushed updates to PR ${prDisplay}${countText}`;
            }
        },
        github_pr_merged: {
            icon: () => TbGitMerge,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const mergedBy = activity.metadata.mergedBy;
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `PR ${prDisplay} was merged${mergedBy ? ` by ${wrapInSpan(mergedBy)}` : ''}`;
            }
        },
        github_pr_closed: {
            icon: (activity) => TbGitPullRequestClosed,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `PR ${prDisplay} was closed without merging`;
            }
        },
        github_review_submitted: {
            icon: (activity) => TbBrandGithub,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const reviewer = activity.metadata.reviewer;
                const reviewState = activity.metadata.reviewState;
                const stateText = reviewState === 'approved' ? 'approved'
                    : reviewState === 'changes_requested' ? 'requested changes on'
                    : reviewState === 'commented' ? 'commented on'
                    : 'reviewed';
                const countText = activity.mergedCount > 1 ? ` (${activity.mergedCount}x)` : '';
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `${wrapInSpan(reviewer)} ${stateText} PR ${prDisplay}${countText}`;
            }
        },
        github_checks_updated: {
            icon: (activity) => TbBrandGithub,
            getMessage: (activity) => {
                const prNumber = activity.metadata.prNumber;
                const checksStatus = activity.metadata.checksStatus;
                const workflowName = activity.metadata.workflowName || 'CI';
                const statusText = checksStatus === 'success' ? 'passed'
                    : checksStatus === 'failure' ? 'failed'
                    : checksStatus;
                const prDisplay = activity.metadata.url ? wrapInLink(`#${prNumber}`, activity.metadata.url) : wrapInSpan(`#${prNumber}`);
                return `${wrapInSpan(workflowName)} ${statusText} for PR ${prDisplay}`;
            }
        }
    };

    const [sortOrder, setSortOrder] = useState('asc');

    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="font-medium">Activity</h3>
                    <div className="flex items-center gap-2">
                        <div className="h-7 w-7 bg-muted animate-pulse rounded" />
                        <Separator orientation="vertical" className="h-5" />
                        <div className="h-7 w-24 bg-muted animate-pulse rounded" />
                    </div>
                </div>
                <div className="space-y-4">
                    {/* Two activities before comment */}
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-32 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>

                    {/* Comment skeleton */}
                    <div className="bg-neutral-200/50 dark:bg-muted/40 rounded-lg p-4 space-y-2">
                        <div className="flex gap-2">
                            <div className="w-4 h-4 rounded-full bg-muted animate-pulse" />
                            <div className="flex items-baseline gap-2">
                                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="h-4 w-full bg-muted animate-pulse rounded" />
                            <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
                        </div>
                    </div>

                    {/* Three activities after comment */}
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-64 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-40 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 pl-4">
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-muted animate-pulse rounded-full" />
                        </div>
                        <div className="flex-grow">
                            <div className="flex gap-2 items-center">
                                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-56 bg-muted animate-pulse rounded" />
                                <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const sortedActivities = (activities || []).slice().sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-medium">Activity</h3>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="text-xs h-7 px-1.5"
                    >
                        {sortOrder === 'asc' ? (
                            <GoArrowUp className="h-4 w-4 mr-1" />
                        ) : (
                            <GoArrowDown className="h-4 w-4 mr-1" />
                        )}
                        {sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
                    </Button>
                </div>
            </div>
            <div className="space-y-4">
                {sortedActivities.map((activity) => {
                    if (activity.type === 'comment_added') {
                        const isOwner = user?.id === activity.actor.id;
                        const isEditing = editingCommentId === activity.id;

                        return (
                            <div key={activity.id} className="group/comment bg-neutral-200/50 dark:bg-muted/40 rounded-lg p-4 space-y-2">
                                <div className="flex gap-2">
                                    <div className="flex-shrink-0">
                                        <div className={`w-4 h-4 rounded-full ${activity.actor.avatarColor} flex items-center justify-center`}>
                                            <span className="text-[0.6rem] font-medium text-white">
                                                {activity.actor.initials}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-baseline gap-2 text-xs text-neutral-500 dark:text-neutral-500 flex-1">
                                        <span className="font-medium text-xs text-neutral-900 dark:text-neutral-200">
                                            {activity.actor.name}
                                        </span>
                                        <span className="text-xs">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger className="text-xs">
                                                        {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <span className="font-medium text-xs">
                                                            {new Date(activity.createdAt).toLocaleString()}
                                                        </span>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </span>
                                        {activity.editedAt && (
                                            <span className="text-xs text-neutral-400 dark:text-neutral-500">
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger className="text-xs">
                                                            (edited)
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <span className="font-medium text-xs">
                                                                {new Date(activity.editedAt).toLocaleString()}
                                                            </span>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </span>
                                        )}
                                        {isOwner && !isEditing && (
                                            <button
                                                onClick={() => setEditingCommentId(activity.id)}
                                                className="ml-auto opacity-0 group-hover/comment:opacity-100 transition-opacity text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                                            >
                                                <FiEdit className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {isEditing ? (
                                    <EditableComment
                                        activity={activity}
                                        task={task}
                                        onTaskChange={onTaskChange}
                                        onCancel={() => setEditingCommentId(null)}
                                        onSaved={() => setEditingCommentId(null)}
                                    />
                                ) : (
                                    <CommentRenderer content={activity.comment.json} />
                                )}
                            </div>
                        );
                    }

                    // Regular activity items
                    try {
                        const config = activityConfig[activity.type] || defaultConfig;
                        const Icon = typeof config.icon === 'function' ? config.icon(activity) : config.icon;
                        
                        // Extract comment text for task_closed activities
                        let commentText = null;
                        let truncatedCommentText = null;
                        if (activity.type === 'task_closed' && activity.comment) {
                            if (activity.comment.content) {
                                commentText = activity.comment.content;
                            } else if (activity.comment.json) {
                                // Extract plain text from tiptap JSON
                                const extractText = (node) => {
                                    if (typeof node === 'string') return node;
                                    if (node?.type === 'text' && node.text) return node.text;
                                    if (node?.content && Array.isArray(node.content)) {
                                        return node.content.map(extractText).join(' ');
                                    }
                                    return '';
                                };
                                commentText = extractText(activity.comment.json);
                            }
                            // Truncate to approximately 50 characters for inline display
                            if (commentText && commentText.length > 50) {
                                truncatedCommentText = commentText.substring(0, 47) + '...';
                            } else {
                                truncatedCommentText = commentText;
                            }
                        }
                        
                        return (
                            <div key={activity.id} className="flex gap-2 pl-4">
                                <div className="flex-shrink-0">
                                    {React.createElement(typeof Icon === 'function' ? Icon : Icon.type, {
                                        className: `w-4 h-4 ${
                                            activity.type === 'priority_changed'
                                                ? PRIORITY_MAP[activity.metadata.to.toLowerCase()].color
                                                : activity.type === 'status_changed'
                                                ? statusConfig[activity.metadata.to].color
                                                : activity.type === 'task_closed'
                                                ? 'text-red-600 dark:text-red-400'
                                                : activity.type === 'github_pr_closed'
                                                ? 'text-red-600 dark:text-red-400'
                                                : activity.type === 'github_review_submitted' || activity.type === 'github_checks_updated'
                                                ? 'text-neutral-600 dark:text-neutral-400'
                                                : 'text-muted-foreground'
                                        }`
                                    })}
                                </div>
                                <div className="flex-grow">
                                    <p className="text-xs text-neutral-600 dark:text-neutral-400 max-w-full truncate">
                                        {activity.actor && !activity.actor.isBot ? (
                                            <>
                                                <span className="font-medium text-neutral-700 dark:text-neutral-200">
                                                    {activity.actor.name}
                                                    {activity.type === 'task_created' && activity.metadata.createdFromSubmission && <> & StatusBot</>}
                                                </span>
                                                {" "}
                                            </>
                                        ) : activity.actor?.isBot && ['github_commit_linked', 'github_pr_linked', 'github_pr_opened'].includes(activity.type) ? (
                                            <>
                                                <span className="font-medium text-neutral-700 dark:text-neutral-200">StatusBot</span>
                                                {" "}
                                            </>
                                        ) : null}
                                        <span dangerouslySetInnerHTML={{ __html: config.getMessage(activity) }} />
                                        {truncatedCommentText && (
                                            <>
                                                {" "}
                                                <span className="text-neutral-500 dark:text-neutral-400 italic">
                                                    "{truncatedCommentText}"
                                                </span>
                                            </>
                                        )}
                                        {" "}
                                        <span className="text-neutral-600 dark:text-neutral-500">
                                            - <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger className="text-xs">
                                                        {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        {new Date(activity.createdAt).toLocaleString()}
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </span>
                                    </p>
                                </div>
                            </div>
                        );
                    } catch(e) {
                        console.error('Something went wrong while rendering this activity:', e);
                        return (
                            <div key={activity.id} className="flex gap-2 pl-4">
                                <div className="text-xs text-neutral-600 dark:text-neutral-400" data-debug-activity={JSON.stringify(activity)}>
                                    Something went wrong while rendering this activity
                                </div>
                            </div>
                        )
                    }
                })}
            </div>
        </div>
    );
} 